import { ReactFCWithChildren, useEffect } from 'react';
import Money from 'src/components/Money';
import {
  Option,
  TMoney,
  Currency,
  OrderItemAddon,
  EnumPriceUnit,
  EnumOptionKind,
  Transport,
  OptionPriceCalculation,
  OrderItem,
  OrderItemAccommodationRoom,
  EnumOptionAccommodationSaleType,
  EnumAccommodationRoomOccupancyType,
} from 'src/types';
import { ApiRoutes } from 'src/lib/routes';
import useQuery from 'src/hooks/useQuery';
import { mergeRecommendedAccommodationRooms } from 'src/components/option/ModalSelectAccommodation';
import { AiFillGift } from 'react-icons/ai';
import { IoMdPricetag } from 'react-icons/io';
import { useTranslations } from 'next-intl';

const mergeOrderItemAccommodationRooms = (
  orderItemAccommodationRooms: OrderItemAccommodationRoom[],
  option: Option
) => {
  return mergeRecommendedAccommodationRooms(orderItemAccommodationRooms, option).map((o) => {
    const orderItemAccommodationRoom = orderItemAccommodationRooms.find(
      (v) => v.accommodationRoomId === o.accommodationRoomId
    ) as OrderItemAccommodationRoom;
    return {
      ...o,
      accommodationRoomId: orderItemAccommodationRoom.accommodationRoomId,
      accommodationRoomName: orderItemAccommodationRoom.accommodationRoomName,
      roomQty: orderItemAccommodationRoom.roomQty,
      amount: o.amount,
    };
  });
};

type PriceBreakdownItemProps = {
  wrapperClassName?: string;
  labelClassName?: string;
  contentClassName?: string;
  label: string | JSX.Element;
};

export const Line: ReactFCWithChildren<PriceBreakdownItemProps> = ({
  wrapperClassName = 'tw-flex tw-justify-between',
  labelClassName = 'tw-text-sm',
  contentClassName = 'tw-text-sm tw-text-right',
  label,
  children,
}) => {
  return (
    <div className={wrapperClassName}>
      <div className={labelClassName}>{label}</div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
};

type Props = {
  isGiftExperience?: boolean;
  currency: Currency;
  displayCurrency: Currency;
  quantity: number;
  childQuantity: number;
  option: Option;
  numNights: number;
  orderItemAccommodationRooms: OrderItemAccommodationRoom[];
  transport?: Transport;
  addons: OrderItemAddon[];
  giftCode?: string | null;
  giftCodeAmount?: TMoney;
  promoCode?: string | null;
  promoCodeDiscountAmount?: TMoney;
  referralAmount?: TMoney;
  creditAmount?: TMoney;
  orderItem?: OrderItem;
  calculationData?: {
    amount: TMoney;
    childAmount: TMoney;
    localDiscountAmount: TMoney;
    transportAmount: TMoney;
  };
  bookingFormValues?: {
    localQuantity: number;
    childLocalQuantity: number;
    startDate: string | null;
    endDate: string | null;
    hasLocal: boolean;
  };
  lineWrapperClassName?: string;
  lineLabelClassName?: string;
  lineContentClassName?: string;
  onFetched?: (data: OptionPriceCalculation) => void;
};

const PriceBreakdown: React.FC<Props> = ({
  isGiftExperience,
  option,
  currency,
  displayCurrency,
  numNights,
  quantity,
  childQuantity,
  orderItemAccommodationRooms,
  transport,
  addons,
  giftCode,
  giftCodeAmount,
  promoCode,
  promoCodeDiscountAmount,
  referralAmount,
  creditAmount,
  calculationData,
  bookingFormValues,
  lineLabelClassName,
  lineWrapperClassName,
  lineContentClassName,
  onFetched,
}) => {
  const translation = useTranslations('components.PriceBreakdown')
  const canCalculate = !calculationData && !!bookingFormValues;
  const apiUrl = ApiRoutes.apiOptionCalculatePriceRoute(
    { id: option.slugCode },
    {
      currency: currency,
      quantity: quantity,
      childQuantity: childQuantity,
      localQuantity: bookingFormValues?.localQuantity,
      childLocalQuantity: bookingFormValues?.childLocalQuantity,
      startDate: bookingFormValues?.startDate,
      endDate: bookingFormValues?.endDate,
      hasLocal: bookingFormValues?.hasLocal,
      transportId: transport?.id,
      isBookingFormCalculation: true,
      orderItemAccommodationRooms: orderItemAccommodationRooms,
      addons: addons.map((o) => ({ id: o.addonId, qty: o.qty })),
    }
  ).toUrl();
  const { data, isLoading } = useQuery<OptionPriceCalculation>(canCalculate ? apiUrl : null);
  const canShow = (canCalculate || calculationData) && !isLoading;
  const hasConversionFee = !isGiftExperience && option.baseCurrency !== currency;

  const { amount, childAmount, localDiscountAmount, transportAmount } = (data ||
    calculationData ||
    {}) as OptionPriceCalculation;

  useEffect(() => {
    if (data && typeof onFetched === 'function') {
      onFetched(data);
    }
  }, [data]);

  return !canShow ? (
    <></>
  ) : (
    <>
      {orderItemAccommodationRooms &&
      orderItemAccommodationRooms.length > 0 &&
      option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom ? (
        mergeOrderItemAccommodationRooms(orderItemAccommodationRooms, option)
          .filter((o) => o.qty > 0)
          .map((o) => (
            <Line
              key={o.accommodationRoomId}
              labelClassName={lineLabelClassName}
              wrapperClassName={lineWrapperClassName}
              contentClassName={lineContentClassName}
              label={o.occupancyType === EnumAccommodationRoomOccupancyType.shared ? `${o.numOfPerson} x ${translation('bedIn')} ${o.accommodationRoomName}` : `${o.qty} x ${o.accommodationRoomName}`}
            >
              <Money
                hasConversionFee={hasConversionFee}
                amount={o.amount}
                toCurrency={displayCurrency}
              />
            </Line>
          ))
      ) : (
        <>
          {option.kind === EnumOptionKind.stay ? (
            <Line
              labelClassName={lineLabelClassName}
              wrapperClassName={lineWrapperClassName}
              contentClassName={lineContentClassName}
              label={translation('pluralizeNight', {count: numNights > 0 ? numNights : 1})}
            >
              <span>
                <Money
                  hasConversionFee={hasConversionFee}
                  amount={amount}
                  toCurrency={displayCurrency}
                />
              </span>
            </Line>
          ) : option.kind === EnumOptionKind.flexibleAdventure ? (
            <Line
              labelClassName={lineLabelClassName}
              wrapperClassName={lineWrapperClassName}
              contentClassName={lineContentClassName}
              label={translation('pluralizeDay', {count: numNights + 1})}
            >
              <span>
                <Money
                  hasConversionFee={hasConversionFee}
                  amount={amount}
                  toCurrency={displayCurrency}
                />
              </span>
            </Line>
          ) : (
            <>
              {option.useChildPrices ? (
                <>
                  {quantity > 0 && (
                    <Line
                      labelClassName={lineLabelClassName}
                      wrapperClassName={lineWrapperClassName}
                      contentClassName={lineContentClassName}
                      label={translation('adult')}
                    >
                      {quantity + (bookingFormValues?.localQuantity || 0)} x{' '}
                      <span>
                        <Money
                          hasConversionFee={hasConversionFee}
                          amount={amount}
                          toCurrency={displayCurrency}
                        />
                      </span>
                    </Line>
                  )}

                  {childQuantity > 0 && (
                    <Line
                      labelClassName={lineLabelClassName}
                      wrapperClassName={lineWrapperClassName}
                      contentClassName={lineContentClassName}
                      label={translation('child')}
                    >
                      {childQuantity + (bookingFormValues?.childLocalQuantity || 0)} x{' '}
                      <span>
                        <Money
                          hasConversionFee={hasConversionFee}
                          amount={childAmount}
                          toCurrency={displayCurrency}
                        />
                      </span>
                    </Line>
                  )}
                </>
              ) : (
                <Line
                  labelClassName={lineLabelClassName}
                  wrapperClassName={lineWrapperClassName}
                  contentClassName={lineContentClassName}
                  label={option.priceUnit === EnumPriceUnit.perGroup ? translation('adventure') : translation('guest')}
                >
                  {option.priceUnit === EnumPriceUnit.perGroup
                    ? ''
                    : `${quantity + (bookingFormValues?.localQuantity || 0)} x `}
                  <span>
                    <Money
                      hasConversionFee={hasConversionFee}
                      amount={amount}
                      toCurrency={displayCurrency}
                    />
                  </span>
                  {option.priceUnit === EnumPriceUnit.perGroup ? `/${translation('groupOf')} ${quantity}` : ''}
                </Line>
              )}
            </>
          )}

          {localDiscountAmount && Number(localDiscountAmount.amount) > 0 && (
            <Line
              labelClassName={lineLabelClassName}
              wrapperClassName={lineWrapperClassName}
              contentClassName={lineContentClassName}
              label={translation('localDiscount')}
            >
              -
              <span>
                <Money
                  hasConversionFee={hasConversionFee}
                  amount={localDiscountAmount}
                  toCurrency={displayCurrency}
                />
              </span>
            </Line>
          )}
        </>
      )}

      {transport && transportAmount && (
        <Line
          labelClassName={lineLabelClassName}
          wrapperClassName={lineWrapperClassName}
          contentClassName={lineContentClassName}
          label={translation('transportAddon')}
        >
          {Number(transportAmount.amount) > 0 ? (
            <span>
              <Money
                hasConversionFee={hasConversionFee}
                amount={transportAmount}
                toCurrency={displayCurrency}
              />
            </span>
          ) : (
            <span>-</span>
          )}
        </Line>
      )}

      {addons &&
        addons.map((addon) => (
          <Line
            labelClassName={lineLabelClassName}
            wrapperClassName={lineWrapperClassName}
            contentClassName={lineContentClassName}
            key={addon.id}
            label={addon.name}
          >
            {addon.qty} x{' '}
            <span>
              <Money
                hasConversionFee={hasConversionFee}
                amount={addon.amount}
                toCurrency={displayCurrency}
              />
            </span>
          </Line>
        ))}

      {giftCodeAmount && Number(giftCodeAmount.amount) > 0 ? (
        <Line
          labelClassName={lineLabelClassName}
          wrapperClassName={lineWrapperClassName}
          contentClassName={lineContentClassName}
          label={
            <>
              {giftCode ? (
                <span className="tw-leading-none">
                  <AiFillGift size={14} className="tw-text-orange tw-mb-0.5" /> {translation('gift')} ({giftCode})
                </span>
              ) : (
                translation('giftCode')
              )}
            </>
          }
        >
          -{' '}
          <span>
            <Money
              amount={giftCodeAmount}
              toCurrency={displayCurrency}
            />
          </span>
        </Line>
      ) : null}

      {promoCodeDiscountAmount && Number(promoCodeDiscountAmount.amount) > 0 ? (
        <Line
          labelClassName={lineLabelClassName}
          wrapperClassName={lineWrapperClassName}
          contentClassName={lineContentClassName}
          label={
            <>
              <span>
                <IoMdPricetag size={14} className="tw-text-orange" /> {promoCode}
              </span>
            </>
          }
        >
          -{' '}
          <span>
            <Money
              amount={promoCodeDiscountAmount}
              toCurrency={displayCurrency}
            />
          </span>
        </Line>
      ) : null}

      {referralAmount && Number(referralAmount.amount) > 0 ? (
        <Line
          labelClassName={lineLabelClassName}
          wrapperClassName={lineWrapperClassName}
          contentClassName={lineContentClassName}
          label={
            <>
              {promoCode ? (
                <span>
                  <IoMdPricetag size={14} className="tw-text-orange" /> {promoCode}
                </span>
              ) : (
                translation('referral')
              )}
            </>
          }
        >
          -{' '}
          <span>
            <Money
              amount={referralAmount}
              toCurrency={displayCurrency}
            />
          </span>
        </Line>
      ) : null}

      {creditAmount && Number(creditAmount.amount) > 0 && (
        <Line
          labelClassName={lineLabelClassName}
          wrapperClassName={lineWrapperClassName}
          contentClassName={lineContentClassName}
          label={translation('credits')}
        >
          -{' '}
          <span>
            <Money amount={creditAmount} toCurrency={currency} />
          </span>
        </Line>
      )}
    </>
  );
};

export default PriceBreakdown;
